import React from "react";
import { Box, Divider, Typography, Chip, Stack } from "@mui/material";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import { parseISO, format } from "date-fns";
import { formatNumber } from "../../../lib/formatNumber";

/** Matches mobile Activities card intent; distinct green so it doesn't read like blue “health & macros”. */
export const HEALTH_ACTIVITY_ACCENT = "#16A34A";

function parseActivityWhen(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value.toDate === "function") {
    const d = value.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === "string") {
    const d = parseISO(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function formatActivityTimeRange(activity) {
  const start = parseActivityWhen(activity.startDate);
  const end = parseActivityWhen(activity.endDate);
  if (start && end) {
    return `${format(start, "h:mm a")}–${format(end, "h:mm a")}`;
  }
  if (start) return format(start, "h:mm a");
  return null;
}

/** @param {number | string | undefined} meters */
export function metersToReadableMiles(meters) {
  const m = Number(meters);
  if (!Number.isFinite(m) || m <= 0) return null;
  const miles = m / 1609.34;
  return miles >= 0.1 ? `${miles.toFixed(1)} mi` : null;
}

/**
 * Apple Health –style workouts synced into `reportHealth.reportData.activities` (parity with mobile app).
 *
 * @param {{ activities?: unknown[], showDivider?: boolean }} props
 */
function SyncedHealthActivitiesSection({ activities, showDivider = false }) {
  const list = Array.isArray(activities) ? activities : [];
  if (list.length === 0) return null;

  const totalMinutes = list.reduce((sum, a) => sum + (Number(a.durationMinutes) || 0), 0);
  const totalCalories = list.reduce((sum, a) => sum + (Number(a.activeCalories) || 0), 0);

  const chipSx = {
    borderColor: `${HEALTH_ACTIVITY_ACCENT}55`,
    color: HEALTH_ACTIVITY_ACCENT,
    bgcolor: `${HEALTH_ACTIVITY_ACCENT}12`,
    height: 24,
    "& .MuiChip-label": { px: 0.75 },
  };

  return (
    <Box sx={{ width: "100%" }}>
      {showDivider ? <Divider sx={{ my: 2 }} /> : null}
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <DirectionsRunIcon sx={{ color: HEALTH_ACTIVITY_ACCENT, fontSize: 28, mt: 0.25 }} />
          <Box>
            <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.8, display: "block" }}>
              Health activities
            </Typography>
            <Typography variant="subtitle1" fontWeight={800} color="text.primary">
              {list.length} {list.length === 1 ? "activity" : "activities"}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" flexWrap="wrap" justifyContent={{ xs: "flex-start", sm: "flex-end" }} sx={{ gap: 0.75 }}>
          {totalMinutes > 0 && (
            <Chip size="small" variant="outlined" sx={chipSx} label={`${formatNumber(totalMinutes, 0)} min total`} />
          )}
          {totalCalories > 0 && (
            <Chip size="small" variant="outlined" sx={chipSx} label={`${formatNumber(totalCalories, 0)} kcal total`} />
          )}
        </Stack>
      </Stack>

      <Box
        sx={{
          borderRadius: 1,
          bgcolor: `${HEALTH_ACTIVITY_ACCENT}14`,
          border: (t) => `1px solid ${t.palette.divider}`,
          px: 1,
        }}
      >
        {list.map((activity, index) => {
          const milesLabel = metersToReadableMiles(activity.distanceMeters);
          const timeRange = formatActivityTimeRange(activity);
          const durMin = Number(activity.durationMinutes) || 0;
          const cals = Number(activity.activeCalories) || 0;
          return (
            <Box
              key={`${activity?.activityId ?? "a"}-${activity?.startDate ?? ""}-${index}`}
              sx={{
                py: 1.5,
                ...(index > 0
                  ? {
                      borderTopWidth: 1,
                      borderTopStyle: "solid",
                      borderColor: "divider",
                    }
                  : {}),
              }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: timeRange ? 0.25 : 0 }}>
                    {activity.activityName || "Unknown activity"}
                  </Typography>
                  {activity.sourceApp ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
                      via {activity.sourceApp}
                    </Typography>
                  ) : null}
                  {timeRange ? (
                    <Typography variant="caption" color="text.secondary">
                      {timeRange}
                    </Typography>
                  ) : null}
                </Box>
                <Stack direction="row" flexWrap="wrap" justifyContent="flex-end" sx={{ gap: 0.75 }}>
                  {durMin > 0 ? (
                    <Chip size="small" variant="outlined" sx={chipSx} label={`${formatNumber(durMin, 0)} min`} />
                  ) : null}
                  {milesLabel ? (
                    <Chip size="small" variant="outlined" sx={chipSx} label={milesLabel} />
                  ) : null}
                  {cals > 0 ? (
                    <Chip size="small" variant="outlined" sx={chipSx} label={`${formatNumber(cals, 0)} kcal`} />
                  ) : null}
                </Stack>
              </Stack>
            </Box>
          );
        })}
      </Box>

      <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 1 }}>
        Synced from Apple Health (Fitness)
      </Typography>
    </Box>
  );
}

export default SyncedHealthActivitiesSection;
